// @ts-nocheck
import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.#setupDesktopClickOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  #setupDesktopClickOpen = () => {
    if (window.innerWidth < 900) return;
    const headerMenu = document.querySelector('header-menu');
    const menuList = headerMenu?.querySelector('.menu-list');

    if (!headerMenu || !menuList) return;

    const menuLinks = menuList.querySelectorAll('.menu-list__link[aria-haspopup="true"]');
    const moreItem = menuList.querySelector('[slot="more"]');
    let closeTimeout = null;

    const closeAllMenus = () => {
      menuLinks.forEach((link) => {
        const listItem = link.closest('.menu-list__list-item');
        const submenu = listItem?.querySelector('.menu-list__submenu');
        link.setAttribute('aria-expanded', 'false');
        link.blur(); // remove focus
        submenu?.classList.remove('is-active');
      });
      // Also properly deactivate the overflow (More) menu state
      this.#debouncedDeactivate.cancel();
      this.#deactivate();
      // Remove --submenu-height so regular submenus revert to clip-path: none
      this.style.removeProperty('--submenu-height');
    };

    menuLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        // Skip if this link has since been moved to the overflow slot
        if (link.closest('[slot="overflow"]')) return;

        const listItem = link.closest('.menu-list__list-item');
        const submenu = listItem?.querySelector('.menu-list__submenu');
        const grid = submenu?.querySelector('.mega-menu__grid');
        if (!submenu || !grid) return;

        const isOpen = link.getAttribute('aria-expanded') === 'true';
        if (!isOpen) {
          event.preventDefault();
          closeAllMenus();
          link.setAttribute('aria-expanded', 'true');
          submenu.classList.add('is-active');
        } else {
          return;
        }
      });
    });

    // Handle clicks on overflow-slot items separately — don't close the overflow panel
    menuList.addEventListener('click', (event) => {
      const link = event.target.closest('[slot="overflow"] .menu-list__link[aria-haspopup]');
      if (!link) return;

      event.preventDefault();
      const isOpen = link.getAttribute('aria-expanded') === 'true';

      // Collapse any other expanded overflow item
      menuList.querySelectorAll('[slot="overflow"] .menu-list__link[aria-expanded="true"]').forEach((l) => {
        if (l !== link) l.setAttribute('aria-expanded', 'false');
      });

      link.setAttribute('aria-expanded', isOpen ? 'false' : 'true');

      // Update --submenu-height so the ::after background matches the new content height
      requestAnimationFrame(() => {
        const overflowPanel = this.overflowMenu;
        if (overflowPanel) {
          this.style.setProperty('--submenu-height', `${overflowPanel.offsetHeight}px`);
        }
      });
    });

    if (moreItem) {
      moreItem.addEventListener('click', (event) => {
        // Close any open mega menus first (without touching overflow state)
        menuLinks.forEach((link) => {
          const listItem = link.closest('.menu-list__list-item');
          const submenu = listItem?.querySelector('.menu-list__submenu');
          link.setAttribute('aria-expanded', 'false');
          link.blur();
          submenu?.classList.remove('is-active');
        });
        // Then activate the overflow — activate() cancels any pending deactivation
        this.activate(event);
      });
    }

    document.addEventListener('mousemove', (e) => {
      const target = e.target;
      const insideHeader = target.closest('header-menu');
      const insideMegaMenu = target.closest('.mega-menu__grid');

      if (!insideHeader && !insideMegaMenu) {
        if (!closeTimeout) {
          closeTimeout = setTimeout(() => {
            closeAllMenus();
            closeTimeout = null;
          }, 150); // delay to avoid flicker
        }
      } else {
        if (closeTimeout) {
          clearTimeout(closeTimeout);
          closeTimeout = null;
        }
      }
    });
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = (event.target.closest?.('[slot]')?.slot ?? '') === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(
      () => {
        item.removeAttribute('data-animating');
      },
      Math.max(0, this.animationDelay - 150),
    ); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreEl = element.closest?.('[slot="more"]');
  if (moreEl) {
    // Select the first overflowing menu item when clicking the "More" button
    return findMenuItem(moreEl.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
